import { makeAutoObservable, runInAction } from 'mobx'
import moment from 'moment'

// evertel
import { decorate, injectable, inject } from '@evertel/di'
import { Api } from '@evertel/api'


import { LocalAsyncStorage } from '@evertel/utils'

// This must match ConsumeInviteController key
const PENDING_INVITE_KEY = 'pendingInviteToken'
const CALLBACK_URL_KEY = 'callbackUrl'
const CALLBACK_URL_TTL_MS = 10 * 60 * 1000 // 10 minutes

class SessionService {
    currentUserId = 0 //if currentUserId === 0 we're logged out
    selectedDepartmentId = 0
    deviceId = 0
    navigationHistory = {}

    // this is required to list any rooms in drawer
    roomIdsIBelongTo: number[] = []
    tokenTTL = 0
    tokenUpdatedDate: Date = undefined
    pendingInviteToken: string | null = null
    callbackUrl: string | null = null

    // DebugFlag
    debugEnabled = false
    testEnabled = false

    private _recentDepartmentIds: number[] = []

    constructor(
        private api: Api,
        private localStorage: LocalAsyncStorage
    ) {
        makeAutoObservable(this)

        // Load debugEnabled value
        this.localStorage.getObject('debugEnabled', false).then(val => {
            runInAction(() => { this.debugEnabled = val })
        }).catch()
        this.localStorage.getObject('testEnabled', false).then(val => {
            runInAction(() => { this.testEnabled = val })
        }).catch()
    }

    init = async () => {
        // Run all localStorage operations concurrently with error handling
        const [
            apiEnv,
            storedUserId,
            deviceId,
            accessToken,
            navigationHistory,
            selectedDepartmentId,
            roomIdsIBelongTo,
            pendingInviteToken,
            storedCallbackUrl
        ] = await Promise.allSettled([
            this.localStorage.getItem('apiEnv'),
            this.localStorage.getItem('userId'),
            this.localStorage.getItem('deviceId'),
            this.localStorage.getItem('authorization'),
            this.localStorage.getItem('navigationHistory'),
            this.localStorage.getItem('selectedDepartmentId'),
            this.localStorage.getItem('roomIdsIBelongTo'),
            this.localStorage.getItem(PENDING_INVITE_KEY),
            this.localStorage.getObject(CALLBACK_URL_KEY, null)
        ]).then(results => results.map((result, index) => {
            if (result.status === 'rejected') {
                const keys = ['apiEnv', 'userId', 'deviceId', 'authorization', 'navigationHistory', 'selectedDepartmentId', 'roomIdsIBelongTo', 'pendingInviteToken', 'callbackUrl']
                console.error(`Failed to get ${keys[index]} from localStorage:`, result.reason)
                return null
            }
            return result.value
        })).catch(error => {
            console.error('Error during localStorage operations:', error)
            return [null, null, null, null, null] // fallback values
        })

        // Update all state in a single runInAction to prevent multiple re-renders
        runInAction(() => {
            const userId = parseInt(storedUserId || '0')

            // Set deviceId
            if (deviceId) {
                this.deviceId = parseInt(deviceId)
            }

            // Set accessToken
            this.api.setUserId(userId)
            if (apiEnv) {
                this.api.setApiEnv(apiEnv)
            }
            if (accessToken) {
                this.api.setAuthToken(accessToken)
            }

            // Set navigationHistory
            try {
                this.navigationHistory = JSON.parse(navigationHistory || '{}')
            } catch (e) {
                this.navigationHistory = {}
            }

            // Set selectedDepartmentId
            const deptId = parseInt(selectedDepartmentId || 0)
            this.selectedDepartmentId = deptId
            this._recentDepartmentIds.unshift(this.selectedDepartmentId)

            this.roomIdsIBelongTo = roomIdsIBelongTo || []

            this.pendingInviteToken = pendingInviteToken

            // Load callbackUrl if not stale
            if (storedCallbackUrl?.url && storedCallbackUrl?.timestamp) {
                if (Date.now() - storedCallbackUrl.timestamp < CALLBACK_URL_TTL_MS) {
                    this.callbackUrl = storedCallbackUrl.url
                } else {
                    // Stale — clear from storage
                    this.localStorage.removeItem(CALLBACK_URL_KEY)
                }
            }

            // ALWAYS LAST THING SET: Set userId
            this.setCurrentUserId(userId)
        })
    }

    setDeviceId = (deviceId: number) => {
        this.deviceId = deviceId

        this.localStorage.setItem('deviceId', this.deviceId?.toString())
    }

    setCurrentUserId = (userId: number) => {
        this.currentUserId = userId

        this.api.setUserId(userId)

        if (userId !== null && userId !== undefined) {
            this.localStorage.setItem('userId', userId.toString())
        } else {
            this.localStorage.removeItem('userId')
        }
    }

    setAuthToken = (authToken: string) => {
        this.api.setAuthToken(authToken)
        this.localStorage.setItem('authorization', authToken)
    }

    setSelectedDeparmentId = (departmentId: number | null) => {
        console.log('🏘️🏘️🏘️🏘️', departmentId, this.selectedDepartmentId)
        if (departmentId === this.selectedDepartmentId) return

        this.selectedDepartmentId = departmentId

        if (this.selectedDepartmentId) {
            this._recentDepartmentIds.unshift(this.selectedDepartmentId)
            this.localStorage.setItem('selectedDepartmentId', departmentId?.toString())
        } else {
            this.localStorage.removeItem('selectedDepartmentId')
        }

    }

    setPendingInviteToken(token: any) {
        this.pendingInviteToken = token
        if (token) {
            this.localStorage.setItem(PENDING_INVITE_KEY, token)
        } else {
            this.localStorage.removeItem(PENDING_INVITE_KEY)
        }
    }

    setCallbackUrl(url: string | null, timestamp?: number) {
        if (url && url.startsWith('/')) {
            this.callbackUrl = url
            this.localStorage.setObject(CALLBACK_URL_KEY, { url, timestamp: timestamp ?? Date.now() })
        } else {
            this.callbackUrl = null
            this.localStorage.removeItem(CALLBACK_URL_KEY)
        }
    }

    /**
     * Returns the callback URL if present and not stale, then clears it.
     * Returns null if stale or not set.
     */
    consumeCallbackUrl(): string | null {
        const url = this.callbackUrl

        // Always clear
        this.callbackUrl = null
        this.localStorage.removeItem(CALLBACK_URL_KEY)

        return url
    }

    /**
     * Re-read pendingInviteToken and callbackUrl from localStorage.
     * Covers cross-tab scenarios where another tab/window wrote these
     * values after this tab's init() already ran.
     * Only hydrates if the in-memory observable is empty so we never
     * overwrite a value that was set during the current session.
     */
    rehydratePendingState = async () => {
        const [pendingInviteToken, storedCallbackUrl] = await Promise.all([
            this.localStorage.getItem(PENDING_INVITE_KEY),
            this.localStorage.getObject(CALLBACK_URL_KEY, null)
        ])

        runInAction(() => {
            if (pendingInviteToken && !this.pendingInviteToken) {
                this.pendingInviteToken = pendingInviteToken
            }

            if (storedCallbackUrl?.url && storedCallbackUrl?.timestamp && !this.callbackUrl) {
                if (Date.now() - storedCallbackUrl.timestamp < CALLBACK_URL_TTL_MS) {
                    this.callbackUrl = storedCallbackUrl.url
                } else {
                    this.localStorage.removeItem(CALLBACK_URL_KEY)
                }
            }
        })
    }

    fetchAccessTokenData = async () => {
        const authToken = await this.api.authToken
        const filter = {
            where: {
                id: authToken
            }
        }
        try {
            const response = await this.api.Routes.BlueUser.getAccessTokens(this.currentUserId, filter) as any[]
            const { ttl, updated } = response[0]

            runInAction(() => {
                this.tokenTTL = ttl
                this.tokenUpdatedDate = updated
            })
        }
        catch (e) {
            runInAction(() => {
                this.tokenTTL = 0
                this.tokenUpdatedDate = moment(0).toDate()
            })
            throw e
        }
    }

    /**
     * Segregate roomStore data to know which are users specific rooms,
     * for use in the Message DrawerController and for interagency rooms
     * @param roomId
     */
    addRoomIdIBelongTo = (roomId: number) => {
        if (!this.roomIdsIBelongTo.find(item => item === roomId)) {
            this.roomIdsIBelongTo.push(roomId)
            this.localStorage.setObject('roomIdsIBelongTo', this.roomIdsIBelongTo)
        }
    }
    setRoomIdsIBelongTo = (roomIds: number[]) => {
        this.roomIdsIBelongTo = roomIds
        this.localStorage.setObject('roomIdsIBelongTo', this.roomIdsIBelongTo)
    }



    /************************************************
     *
     * Navigation History Related Items
     *
     ************************************************/

    get currentPathname() {
        // returns current path within the selected department
        const deptHistory = this.navigationHistory[this.selectedDepartmentId]
        return (deptHistory) ? deptHistory[0]?.pathname : undefined
    }

    get previousPath() {
        // returns last path within the selected department
        if (!this.selectedDepartmentId || !this.navigationHistory[this.selectedDepartmentId]) return undefined

        const prev = this.navigationHistory[this.selectedDepartmentId][0]
        if (prev && prev.pathname !== '/login') {
            return `${prev.pathname}${prev.params || prev.search || ''}`
        }

        return undefined
    }

    get previousRoomOrThread() {
        // returns last room or thread path and id navigated to within the selected department
        if (!this.selectedDepartmentId) return undefined

        const history = this.navigationHistory[this.selectedDepartmentId]
        if (!history || history.length === 0) return undefined

        const lastLocation = history.find(
            (p) => p.pathname.startsWith('/thread/') || p.pathname.startsWith('/room/')
        )

        if (lastLocation) {
            const { pathname } = lastLocation
            const id = parseInt(pathname.split('/').pop())
            const type = pathname.startsWith('/thread/') ? 'thread' : 'room'

            return {
                path: pathname,
                id,
                type
            }
        }

        return undefined
    }

    get recentDepartmentIds() {
        //remove duplicates and return top 3
        return [...new Set(this._recentDepartmentIds)].slice(0, 3)
    }

    updateNavigationHistory = (location: {pathname: string, params: string}) => {
        // we store navigation history for each department the user belongs to
        const navHistory = this.navigationHistory

        if (!navHistory[this.selectedDepartmentId]) {
            navHistory[this.selectedDepartmentId] = []
        }

        // remove same path from history to prevent duplicates
        navHistory[this.selectedDepartmentId] = navHistory[this.selectedDepartmentId].filter(
            entry => entry.pathname !== location.pathname
        )
        // add latest to front of array
        navHistory[this.selectedDepartmentId].unshift(location)

        // only keep the last 10 routes for each department in navigationHistory
        if (navHistory[this.selectedDepartmentId].length > 10) navHistory[this.selectedDepartmentId].pop()

        this.navigationHistory = navHistory

        this.localStorage.setItem('navigationHistory', JSON.stringify(this.navigationHistory))
    }

    private removeFromHistory = (
        departmentId: number,
        predicate: (entry: { pathname: string }) => boolean
    ) => {
        const history = this.navigationHistory[departmentId]
        if (history) {
            this.navigationHistory[departmentId] = history.filter(entry => !predicate(entry))
            this.localStorage.setItem('navigationHistory', JSON.stringify(this.navigationHistory))
        }
    }

    deleteRoomFromNavigationHistory = (roomId: number, departmentId?: number) => {
        this.removeFromHistory(
            departmentId ?? this.selectedDepartmentId,
            entry => entry.pathname.startsWith(`/room/${roomId}`)
        )
    }

    deleteThreadFromNavigationHistory = (threadId: number, departmentId?: number) => {
        this.removeFromHistory(
            departmentId ?? this.selectedDepartmentId,
            entry => entry.pathname.startsWith(`/thread/${threadId}`)
        )
    }

    setDebugEnabled(val: boolean) {
        this.debugEnabled = val
        this.localStorage.setObject('debugEnabled', val)
    }
    setTestEnabled(val: boolean) {
        this.testEnabled = val
        this.localStorage.setObject('testEnabled', val)
    }

    destroy = () => {
        // sessionService sets the auth tokes,
        this.api.deleteAuthToken()
        this.localStorage.removeItem('authorization')

        this.roomIdsIBelongTo = []
        this.localStorage.removeItem('roomIdsIBelongTo')

        this.navigationHistory = {}
        this.localStorage.removeItem('navigationHistory')

        this.setCurrentUserId(0)
        this.setDeviceId(0)
        this.setSelectedDeparmentId(0)
    }

}

decorate(injectable(), SessionService)
decorate(inject(Api), SessionService, 0)
decorate(inject(LocalAsyncStorage), SessionService, 1)

export { SessionService }
